import { ArrowLeft } from 'lucide-react';
import { FC, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getBrowserLanguage, useLanguageState } from '@/state';

import staffBrandMark from '../../../../assets/images/staff/brand_mark.png';
import staffBrandMark2x from '../../../../assets/images/staff/brand_mark@2x.png';
import staffBrandMark3x from '../../../../assets/images/staff/brand_mark@3x.png';
import { ReactComponent as Logo } from '../../../../assets/logo-hotelspot-dark.svg';
import { i18n } from '../../../../utils/i18n';
import {
  BackButton,
  BackLabelFull,
  BackLabelShort,
  BackSlot,
  BrandLink,
  BrandSlot,
  TopBar,
  TopBarInner,
} from './AuthTopBar.styled';

const HOTELSPOT_URL = 'https://www.hotelspot.pl';

/* Strona hotelspot.pl: polski bez prefiksu, pozostałe języki pod /en, /de (jak w hotelspot-audit). */
const getHotelspotHref = (language: string): string => {
  const lang = language.slice(0, 2);
  return lang === 'pl' ? HOTELSPOT_URL : `${HOTELSPOT_URL}/${lang}`;
};

export type AuthBrand = 'panel' | 'staff';

interface AuthTopBarProps {
  /* 'staff': znak „hotelspot | STAFF” zamiast logo, gdy ekran otwarto z linku aplikacji HotelSpot Staff. */
  brand?: AuthBrand;
}

/* Pasek górny auth: powrót przy lewej krawędzi ekranu, logo przy lewej krawędzi kontenera. Zmiana języka jest w stopce. */
export const AuthTopBar: FC<AuthTopBarProps> = ({ brand = 'panel' }) => {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguageState();
  const currentLanguage = (i18n.language || language || 'pl').slice(0, 2);
  const hotelspotHref = getHotelspotHref(currentLanguage);

  // Pierwsza wizyta: język z przeglądarki. Później: zapisany wybór zsynchronizowany z i18n.
  useLayoutEffect(() => {
    if (!localStorage.getItem('language-storage')) {
      const browserLang = getBrowserLanguage();
      setLanguage(browserLang);
      i18n.changeLanguage(browserLang);
    } else if (language && language !== i18n.language) {
      i18n.changeLanguage(language);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Szerokość przycisku powrotu (z odstępem) dla CSS: logo nie może wjechać pod przycisk.
  const barRef = useRef<HTMLElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const bar = barRef.current;
    const back = backRef.current;
    if (!bar || !back) return undefined;
    const update = () => bar.style.setProperty('--auth-back-w', `${back.offsetLeft + back.offsetWidth + 24}px`);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(back);
    return () => observer.disconnect();
  }, []);

  return (
    <TopBar ref={barRef}>
      <BackSlot ref={backRef}>
        <BackButton href={hotelspotHref}>
          <ArrowLeft aria-hidden="true" />
          <BackLabelFull>{t('auth.topBar.backToHotelspot')}</BackLabelFull>
          <BackLabelShort>{t('auth.topBar.backToHotelspotShort')}</BackLabelShort>
        </BackButton>
      </BackSlot>

      <TopBarInner>
        <BrandSlot>
          {brand === 'staff' ? (
            <BrandLink href={hotelspotHref} aria-label={t('auth.resetPassword.staff.brandLabel', 'HotelSpot Staff')}>
              <img
                src={staffBrandMark}
                srcSet={`${staffBrandMark} 1x, ${staffBrandMark2x} 2x, ${staffBrandMark3x} 3x`}
                width={133}
                height={28}
                alt=""
              />
            </BrandLink>
          ) : (
            <BrandLink href={hotelspotHref} aria-label={t('auth.topBar.logoLabel')}>
              <Logo aria-hidden="true" focusable="false" />
            </BrandLink>
          )}
        </BrandSlot>
      </TopBarInner>
    </TopBar>
  );
};
